import { cn, Text } from '@viapeople/components'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FormLabelWithTooltip } from './FormLabelWithTooltip'
import type { ComponentProps, NineBoxRatingCustomProps } from './types'

const GRIDSIZE = 3
const CELL_MIN_W = 80
const CELL_MIN_H = 64
const GRID_GAP = 4 // matches Tailwind gap-1 (= 0.25rem = 4px)
const GRID_PAD = 16 // matches Tailwind p-4 (= 1rem = 16px)
const GRID_BORDER = 2 // matches Tailwind border-2
// Chrome = everything that isn't cell-content inside the border-box: gaps + padding + border.
const GRID_CHROME = (GRIDSIZE - 1) * GRID_GAP + 2 * GRID_PAD + 2 * GRID_BORDER
// Hard upper bound on visual height; cellMinHeight reports clamp here so a vertical drag can't
// expand the form area indefinitely.
const MAX_HEIGHT_VH = 0.7

type Forms9BoxRatingProps = ComponentProps & {
	// Saved responses are returned from the API as strings (the form-response store coerces all
	// values to text), so accept both shapes and coerce internally.
	value?: number | string
	onChange?: (value: number) => void
	readOnly?: boolean
	hasError?: boolean
	editable?: boolean
	onResize?: (cellWidth: number, cellHeight: number) => void
}

export function Forms9BoxRating({
	component,
	value,
	onChange,
	readOnly,
	required,
	hasError,
	editable,
	onResize,
}: Forms9BoxRatingProps) {
	const {
		label,
		labelColor,
		labelSize,
		labelWeight,
		xAxisLabel,
		yAxisLabel,
		selectionColor,
		boxDefinitions,
		tooltipEnabled,
		tooltipText,
		cellSize,
	} = component.custom as NineBoxRatingCustomProps
	const [internalValue, setInternalValue] = useState<number | null>(null)

	const cellMinWidth = cellSize?.width
	const cellMinHeight = cellSize?.height
	const minWidthStyle = typeof cellMinWidth === 'number' && cellMinWidth > 0 ? `${cellMinWidth}px` : undefined
	const minHeightStyle = typeof cellMinHeight === 'number' && cellMinHeight > 0 ? `${cellMinHeight}px` : undefined

	const wrapperRef = useRef<HTMLDivElement>(null)
	// Holds the in-flight drag's cleanup function (or null). Set when listeners are attached;
	// cleared by the pointerup handler. The unmount effect below calls it so a component removed
	// mid-drag (e.g. deleted from the builder via keyboard shortcut) doesn't leak window listeners.
	const activeDragCleanupRef = useRef<(() => void) | null>(null)

	useEffect(() => {
		return () => {
			activeDragCleanupRef.current?.()
			activeDragCleanupRef.current = null
		}
	}, [])

	// Custom drag-to-resize. We do NOT use native CSS `resize: both` because the browser writes
	// element.style.width directly during the gesture and bypasses any React or CSS clamping —
	// the result was the wrapper expanding past its section column. Instead: pointerdown on the
	// handle, capture the pointer, and on each move compute the new wrapper size, clamp it to
	// the parent column's clientWidth, convert to per-cell sizes, and call onResize. React stays
	// the source of truth for `cellSize`, so the wrapper width on render is always React-clamped.
	const handleResizePointerDown = useCallback(
		(e: React.PointerEvent<HTMLDivElement>) => {
			if (!editable || !onResize || !wrapperRef.current) return
			const wrapper = wrapperRef.current
			const parent = wrapper.parentElement
			if (!parent) return
			// Stop dnd-kit from interpreting the gesture as a component drag-reorder.
			e.stopPropagation()
			e.preventDefault()
			const startX = e.clientX
			const startY = e.clientY
			const startRect = wrapper.getBoundingClientRect()
			const startW = startRect.width
			const startH = startRect.height
			// pointermove events fire on the window so the user can drag past the wrapper bounds.
			const handle = e.currentTarget
			const pointerId = e.pointerId
			handle.setPointerCapture(pointerId)

			const handleMove = (ev: PointerEvent) => {
				if (!parent) return
				const dx = ev.clientX - startX
				const dy = ev.clientY - startY
				// Clamp the wrapper width to the parent column's available space so the user can't
				// drag past the section.
				const maxW = parent.clientWidth
				const maxH = window.innerHeight * MAX_HEIGHT_VH
				const newW = Math.max(CELL_MIN_W * GRIDSIZE + GRID_CHROME, Math.min(maxW, startW + dx))
				const newH = Math.max(CELL_MIN_H * GRIDSIZE + GRID_CHROME, Math.min(maxH, startH + dy))
				const cellW = Math.max(CELL_MIN_W, Math.floor((newW - GRID_CHROME) / GRIDSIZE))
				const cellH = Math.max(CELL_MIN_H, Math.floor((newH - GRID_CHROME) / GRIDSIZE))
				onResize(cellW, cellH)
			}
			const cleanup = () => {
				if (handle.hasPointerCapture(pointerId)) handle.releasePointerCapture(pointerId)
				window.removeEventListener('pointermove', handleMove)
				window.removeEventListener('pointerup', cleanup)
				window.removeEventListener('pointercancel', cleanup)
				activeDragCleanupRef.current = null
			}
			window.addEventListener('pointermove', handleMove)
			window.addEventListener('pointerup', cleanup)
			window.addEventListener('pointercancel', cleanup)
			activeDragCleanupRef.current = cleanup
		},
		[editable, onResize],
	)

	const handleCellClick = (boxNumber: number) => {
		if (readOnly) return

		if (onChange) {
			onChange(boxNumber)
		} else {
			setInternalValue(boxNumber)
		}
	}

	// Coerce to a number so a stringified saved value (e.g. "5" from a reloaded form response)
	// still highlights its cell. `currentValue === boxNumber` would otherwise be false for "5" vs 5.
	const rawCurrentValue = value !== undefined ? value : internalValue
	const numericValue =
		typeof rawCurrentValue === 'number'
			? rawCurrentValue
			: rawCurrentValue !== null && rawCurrentValue !== ''
				? Number(rawCurrentValue)
				: Number.NaN
	const currentValue: number | null = Number.isFinite(numericValue) ? numericValue : null

	const getBoxNumber = (row: number, col: number): number => {
		return row * GRIDSIZE + col + 1
	}

	// Use configured boxDefinitions or fallback to defaults
	const defaultBoxDefinitions = {
		1: { label: 'Bad Hire', color: '#ef4444' },
		2: { label: 'Backup', color: '#ef4444' },
		3: { label: 'Workhorse', color: '#eab308' },
		4: { label: 'Inconsistent', color: '#ef4444' },
		5: { label: 'Core player', color: '#eab308' },
		6: { label: 'High Performer', color: '#3b82f6' },
		7: { label: 'Potential Gem', color: '#eab308' },
		8: { label: 'High Potential', color: '#3b82f6' },
		9: { label: 'Star', color: '#3b82f6' },
	}
	const finalBoxDefinitions = boxDefinitions || defaultBoxDefinitions

	const renderGrid = () => {
		const cells = []
		for (let row = GRIDSIZE - 1; row >= 0; row--) {
			for (let col = 0; col < GRIDSIZE; col++) {
				const boxNumber = getBoxNumber(row, col)
				const isSelected = currentValue === boxNumber
				const boxDef = finalBoxDefinitions[boxNumber as keyof typeof finalBoxDefinitions]

				// In editable mode the grid template enforces the column/row minimums via
				// minmax(min(<cellMin>px, 100%/N), 1fr) — a percentage in min() resolves to 0 in
				// min-content calculations (per CSS Grid spec) so the wrapper can shrink to fit
				// a narrow parent. Inline minWidth/minHeight on cells would defeat that, so apply
				// them only in the non-editable preview where the explicit size is the source of truth.
				const cellMinStyle = editable ? {} : { minWidth: minWidthStyle, minHeight: minHeightStyle }

				cells.push(
					<button
						key={boxNumber}
						type='button'
						className={`
              border border-neutral-300 hover:border-neutral-400
              transition-colors duration-200 focus:outline-none focus:ring-2
              focus:ring-blue-500 focus:ring-opacity-50 flex items-center justify-center
              min-w-[80px] min-h-[64px]
              ${isSelected ? 'ring-2' : ''}
              ${readOnly ? 'cursor-default' : 'cursor-pointer'}
            `}
						style={{
							backgroundColor: isSelected ? 'white' : boxDef.color,
							borderColor: isSelected ? selectionColor : boxDef.color,
							color: isSelected ? selectionColor : 'white',
							...cellMinStyle,
						}}
						onClick={() => handleCellClick(boxNumber)}
						disabled={readOnly}
						aria-label={`Box ${boxNumber}: ${boxDef.label}`}>
						<span className='text-xs font-medium text-center leading-tight px-1'>{boxDef.label}</span>
					</button>,
				)
			}
		}
		return cells
	}

	const gridElement = (
		<div
			ref={wrapperRef}
			data-testid='9box-grid'
			className={cn(
				'relative grid gap-1 p-4 rounded-lg bg-neutral-50 overflow-hidden',
				hasError ? 'border-[2px] border-error-600' : 'border-2 border-neutral-200',
				editable && 'ring-1 ring-primary-300 ring-offset-2 ring-offset-neutral-0',
			)}
			style={{
				// Wrapper width = the smaller of the user-configured size and 100% of the parent
				// column. With our custom drag (no native `resize: both`), React fully controls
				// the inline width so this clamp is always honored.
				width:
					typeof cellMinWidth === 'number' && cellMinWidth > 0
						? `min(${cellMinWidth * GRIDSIZE + GRID_CHROME}px, 100%)`
						: '100%',
				// Use `minHeight` (not `height`) so the wrapper grows to fit a row whose content
				// is taller than the configured size — this is the PR's auto-size guarantee. A
				// fixed `height:` would clip long labels (or push them past the cell border).
				minHeight:
					typeof cellMinHeight === 'number' && cellMinHeight > 0
						? `${cellMinHeight * GRIDSIZE + GRID_CHROME}px`
						: undefined,
				// Track-level minimums use `min(<cellMin>, 100%/N)` for columns so cells aspire to
				// the user-configured width but shrink with a narrow column. Rows use `auto` so each
				// row sizes to the tallest cell's content (and stays at least cellMinHeight via the
				// wrapper's minHeight).
				gridTemplateColumns: `repeat(${GRIDSIZE}, minmax(min(${cellMinWidth ?? CELL_MIN_W}px, calc(100% / ${GRIDSIZE})), 1fr))`,
				gridTemplateRows: `repeat(${GRIDSIZE}, auto)`,
				maxWidth: '100%',
			}}>
			{renderGrid()}
			{editable && onResize && (
				<div
					data-testid='9box-resize-handle'
					onPointerDown={handleResizePointerDown}
					className='absolute bottom-0 right-0 flex h-4 w-4 cursor-nwse-resize items-end justify-end touch-none select-none'>
					<svg width='12' height='12' viewBox='0 0 12 12' className='text-primary-500'>
						<title>Drag to resize</title>
						<path
							d='M11 1 L1 11 M11 5 L5 11 M11 9 L9 11'
							stroke='currentColor'
							strokeWidth='1.5'
							strokeLinecap='round'
						/>
					</svg>
				</div>
			)}
		</div>
	)

	return (
		<div className='flex flex-col gap-4 w-full'>
			{label && (
				<FormLabelWithTooltip
					label={label}
					labelColor={labelColor}
					labelSize={labelSize}
					labelWeight={labelWeight}
					required={required}
					readOnly={readOnly}
					tooltipEnabled={tooltipEnabled}
					tooltipText={tooltipText}
				/>
			)}

			<div className={cn('flex flex-col items-center gap-2', editable && 'w-full')}>
				<div className={cn('flex items-center gap-2 min-w-0', editable && 'w-full')}>
					{/* X-axis label (left) */}
					<div className='flex items-center justify-center w-16 shrink-0'>
						<Text variant='small' className='text-neutral-600 font-medium -rotate-90'>
							{xAxisLabel}
						</Text>
					</div>

					{/* Grid (resize-enabled when editable) */}
					<div className={cn('min-w-0', editable && 'flex-1')}>{gridElement}</div>
				</div>

				{/* Y-axis label (bottom) */}
				<Text variant='small' className='text-neutral-600 font-medium'>
					{yAxisLabel}
				</Text>
			</div>

			<div className='flex justify-center flex-col items-center'>
				{currentValue && currentValue > 0 && (
					<Text variant='small' className='text-neutral-500'>
						Selected: {finalBoxDefinitions[currentValue as keyof typeof finalBoxDefinitions]?.label}
					</Text>
				)}
			</div>

			{readOnly && currentValue && currentValue > 0 && (
				<div className='bg-neutral-50 border border-neutral-200 rounded-md px-6 py-4'>
					<Text>Selected: {finalBoxDefinitions[currentValue as keyof typeof finalBoxDefinitions]?.label}</Text>
				</div>
			)}
		</div>
	)
}
